import { Spacing } from '@persona-id/design-tokens';
import { omit, pipe, set } from 'lodash/fp';
import { Suspense, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { Navigate, Route } from 'react-router-dom-v5-compat';
import { getFormValues } from 'redux-form';
import styled from 'styled-components';

import useIsEditableWithOptions from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import { generatePages } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages';
import { HandleSaveInquiryTemplateVersionProps } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import InquiryTemplateEditorToolbar, {
  TOOLBAR_HEIGHT,
  TOOLBAR_TRANSITION,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar';
import { firstInitialStep } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import InquiryFlowPreview from 'dashboard/components/InquiryFlowPreview';
import LocalizedDirection from 'dashboard/components/LocalizedDirection';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import ThemeEditorNav from 'dashboard/components/ThemeEditorNav';
import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import { FULL_SCREEN_HEADER_HEIGHT } from 'dashboard/layouts/ShowViewLayoutV2';
import {
  FORM_THEME_EDITOR_ADVANCED_NAME,
  FORM_THEME_EDITOR_BUTTONS_NAME,
  FORM_THEME_EDITOR_COLOR_NAME,
  FORM_THEME_EDITOR_COMPLETION_PAGE_NAME,
  FORM_THEME_EDITOR_DOCUMENTS_UPLOAD_PAGE_NAME,
  FORM_THEME_EDITOR_FAILED_PAGE_NAME,
  FORM_THEME_EDITOR_FONT_NAME,
  FORM_THEME_EDITOR_SELECT_COUNTRY_PAGE_NAME,
  FORM_THEME_EDITOR_START_PAGE_LOGO_NAME,
} from 'dashboard/lib/constants/formNames';
import { mergeResourceWithFormValues } from 'dashboard/lib/utils/formData';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStep,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import { Template } from 'dashboard/models/template';
import { Theme, ThemeAttributes } from 'dashboard/models/theme';
import { isThemeEditorFormDirty } from 'dashboard/redux/selectors/theme';
import { RootState } from 'dashboard/redux/store';
import DashboardEditorAdvancedView from 'dashboard/views/DashboardEditorAdvancedView';
import DashboardEditorButtonsView from 'dashboard/views/DashboardEditorButtonsView';
import DashboardEditorColorView from 'dashboard/views/DashboardEditorColorView';
import DashboardEditorFontView from 'dashboard/views/DashboardEditorFontView';
import DashboardEditorStartLogoView from 'dashboard/views/DashboardEditorStartLogoView';
import { defaultColorAttributes } from 'dashboard/views/DashboardEditorView/DashboardEditorView';
import {
  headerComponentIndex,
  mapStepsConfigToStepsPreview,
} from 'dashboard/views/DashboardInquiryTemplatesShowView/utils';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { INQUIRY_LANGUAGES_ENGLISH, InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { readFile } from 'lib/file';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import type { StepPreview } from 'lib/interfaces/StepsConfig';
import { dataUrlToBase64 } from 'lib/utils/dataUrl';

export interface InquiryTemplateThemeFormData {
  'asset-data'?: string;
  'asset-filename'?: string;
  steps: InquiryTemplateVersionStep[];
  theme: ThemeAttributes;
}

export type InqTmplVersionWithAssetFile = InquiryTemplateVersion & {
  attributes: { 'asset-file'?: FileWithPreview };
};
type InqTmplVersionWithProcessedAsset = InquiryTemplateVersion & {
  attributes: { 'asset-data'?: string; 'asset-filename'?: string };
};

/**
 * borrowed from dashboard/routes/DashboardEditorRoute/DashboardEditorRoute.tsx
 */
const processInqTmplVersionLogo = async (
  inquiryTemplateVersion: InqTmplVersionWithAssetFile,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
): Promise<InqTmplVersionWithProcessedAsset> => {
  try {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const file: FileWithPreview | null = inquiryTemplateVersion.attributes['asset-file'];
    const assetData = file
      ? await new Promise<string>((res) => {
          const imgBuffer = document.createElement('img');
          imgBuffer.onload = async () => {
            res(dataUrlToBase64(imgBuffer.src));
          };

          readFile(file).then((result) => {
            imgBuffer.src = result as string;
          });
        })
      : null;

    return pipe(
      (t) => omit('attributes.asset-file', t) as InquiryTemplateVersion,
      (t) =>
        assetData
          ? (set('attributes.asset-data', assetData, t) as InquiryTemplateVersion)
          : (omit('attributes.asset-data', t) as InquiryTemplateVersion),
      (t) =>
        assetData
          ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            (set('attributes.asset-filename', file.name, t) as InquiryTemplateVersion)
          : (omit('attributes.asset-filename', t) as InquiryTemplateVersion),
    )(inquiryTemplateVersion);
  } catch (e) {
    // TODO
  }
};

const Container = styled('div')`
  display: flex;
`;

const ConfigSection = styled('div')<{ visible: boolean }>`
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT + TOOLBAR_HEIGHT}px);
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-left: 1px solid ${({ theme }) => theme.color.border.primary};
  flex: ${({ visible }) => (visible ? '0 0 400px' : '0 0 0px')};
  transition:
    flex ${TOOLBAR_TRANSITION},
    transform ${TOOLBAR_TRANSITION};
  // Note (Evan): Applying a transformation on a parent container
  // reverts any positioning of its children back to static.
  // In this case, we want the footer actions to transition
  // along with the parent container, rather than be fixed
  transform: ${({ visible }) => (visible ? '' : 'translateX(-100%)')};

  padding: 0px;
`;

const ConfigContainer = styled('div')`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const TitleContainer = styled('div')`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const PreviewContainer = styled('div')`
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT + 50}px);
  width: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

// Copied from TemplateSelector and ThemeSelector
const SelectContainer = styled('div')`
  padding-left: ${Spacing[4]};
  padding-right: ${Spacing[4]};
  margin-bottom: ${Spacing[1]};
`;

interface InquiryTemplateThemeProps {
  inquiryTemplate: InquiryTemplate;
  inquiryTemplateVersion: InquiryTemplateVersion;
  stepsPreview: StepPreview[];
  currentLocale: InquiryIso6391LanguageCode;
  lastPath: string;
  handleSaveInquiryTemplateVersion: ({
    formValues,
    inquiryTemplate,
    reloadPath,
  }: HandleSaveInquiryTemplateVersionProps) => Promise<void>;
  handleSetLocale: (locale: InquiryIso6391LanguageCode) => void;
}

const InquiryTemplateTheme = ({
  inquiryTemplate,
  inquiryTemplateVersion,
  currentLocale,
  stepsPreview,
  lastPath,
  handleSaveInquiryTemplateVersion,
  handleSetLocale,
}: InquiryTemplateThemeProps) => {
  const [showSidePanel, { toggle: toggleSidePanel }] = useToggle(true);
  const { url: matchUrl } = useRouteMatch();
  const editable = useIsEditableWithOptions();

  const formValues = useSelector<RootState, any>(
    (state) =>
      getFormValues('editor-theme')(state) ??
      getFormValues(FORM_THEME_EDITOR_ADVANCED_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_BUTTONS_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_COLOR_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_COMPLETION_PAGE_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_FAILED_PAGE_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_FONT_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_SELECT_COUNTRY_PAGE_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_DOCUMENTS_UPLOAD_PAGE_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_START_PAGE_LOGO_NAME)(state) ??
      {},
  );

  const hasUnsavedChanges = useSelector<RootState, boolean>(isThemeEditorFormDirty);

  const inqTmplVersionLiveEdited = useMemo<InquiryTemplateVersion>(
    () =>
      mergeResourceWithFormValues<InquiryTemplateVersion>(
        inquiryTemplateVersion,
        formValues.inqTmplVersion,
      ),
    [inquiryTemplateVersion, formValues.inqTmplVersion],
  );

  const stepsPreviewLiveEdited = useMemo<StepPreview[]>(
    () =>
      mapStepsConfigToStepsPreview(
        inqTmplVersionLiveEdited['attributes'],
        stepsPreview,
        hasUnsavedChanges,
      ),
    [inqTmplVersionLiveEdited, stepsPreview, hasUnsavedChanges],
  );

  const pages = useMemo<React.ReactElement[]>(
    () => Object.values(generatePages(stepsPreviewLiveEdited)).flatMap((pages) => pages),
    [stepsPreviewLiveEdited],
  );

  // process asset file, if present, before updating draft
  const updateDraftWithSteps = async (inquiryTemplate: InquiryTemplate) => {
    const processedInqTmplVersion = await processInqTmplVersionLogo(
      inqTmplVersionLiveEdited as InqTmplVersionWithAssetFile,
    );
    const formData: InquiryTemplateThemeFormData = {
      'asset-data': processedInqTmplVersion.attributes['asset-data'],
      'asset-filename': processedInqTmplVersion.attributes['asset-filename'],
      steps: processedInqTmplVersion.attributes.steps,
      theme: themeLiveEdited.attributes,
    };

    await handleSaveInquiryTemplateVersion({
      formValues: formData,
      inquiryTemplate,
      reloadPath: `theme/${lastPath}`,
      lockVersion: inquiryTemplateVersion.attributes['lock-version'],
    });
  };

  const themeSelectedPristine = useMemo<Theme>(
    () => ({
      // Dynamic Flow themes aren't standalone model instances, and don't have IDs.
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      id: undefined,
      type: ApiModel.Theme,
      attributes: inquiryTemplateVersion.attributes.theme,
    }),
    [inquiryTemplateVersion],
  );

  const themeLiveEdited = useMemo<Theme>(
    () => mergeResourceWithFormValues<Theme>(themeSelectedPristine, formValues.theme),
    [themeSelectedPristine, formValues.theme],
  );

  const settings = {
    arrows: false,
    centerMode: true,
    dots: true,
    focusOnSelect: true,
    infinite: false,
    speed: 500,
    swipeToSlide: true,
    variableWidth: true,
  };

  // Casts and stubs are for features irrelevant to inquiry template editor.
  const editorPanelProps = {
    // This is quite hacky. Done because it's hard to add new logic to the theme editor.
    // This is only a stopgap til Inquiry Template Editor.
    handleSave: async () => updateDraftWithSteps(inquiryTemplate),
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    handleTemplateSelect: (): void => null,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    handleThemeSelect: (): void => null,
    loading: false,
    templates: [] as Template[],
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    templateSelectedPristine: undefined as Template,
    themes: [] as Theme[],
    themeSelectedPristine,
    disabled: !editable,
    reactRouterV6: true,
  };

  return (
    <>
      <Suspense fallback={null}>
        <InquiryTemplateEditorToolbar
          leftNavPanel={{
            sidePanelVisible: showSidePanel,
            setSidePanelVisible: toggleSidePanel,
          }}
        />
        <LocalizedDirection>
          <Container>
            <ConfigSection visible={showSidePanel}>
              <SentryRoutes>
                <Route
                  index
                  element={
                    <InquiryTemplateThemeNavSection
                      inquiryTemplateVersion={inquiryTemplateVersion}
                      handleSetLocale={handleSetLocale}
                      locale={currentLocale}
                    />
                  }
                />

                <Route
                  path='start-page-title'
                  element={
                    <DashboardEditorStartLogoView
                      {...editorPanelProps}
                      inqTmplVersionPristine={inquiryTemplateVersion}
                      stepsPreview={stepsPreviewLiveEdited}
                    />
                  }
                />

                <Route
                  path='color'
                  element={
                    <DashboardEditorColorView
                      {...editorPanelProps}
                      defaultColorAttributes={defaultColorAttributes}
                    />
                  }
                />

                <Route path='font' element={<DashboardEditorFontView {...editorPanelProps} />} />

                <Route
                  path='buttons'
                  element={<DashboardEditorButtonsView {...editorPanelProps} />}
                />

                <Route
                  path='advanced'
                  element={
                    <DashboardEditorAdvancedView
                      {...editorPanelProps}
                      defaultColorAttributes={defaultColorAttributes}
                    />
                  }
                />

                <Route path='*' element={<Navigate to={matchUrl} replace />} />
              </SentryRoutes>
            </ConfigSection>
            <PreviewContainer>
              <InquiryFlowPreview
                theme={themeLiveEdited}
                templateId={inqTmplVersionLiveEdited.id}
                pages={pages}
                settings={settings}
                showProgressBar={false}
              />
            </PreviewContainer>
          </Container>
        </LocalizedDirection>
      </Suspense>
    </>
  );
};

interface InquiryTemplateThemeNavSectionProps {
  inquiryTemplateVersion: InquiryTemplateVersion;
  handleSetLocale: (locale: InquiryIso6391LanguageCode) => void;
  locale: InquiryIso6391LanguageCode;
}

const InquiryTemplateThemeNavSection = ({
  inquiryTemplateVersion,
  handleSetLocale,
  locale,
}: InquiryTemplateThemeNavSectionProps) => {
  const showStartLogoPage = useMemo<boolean>(() => {
    const steps = inquiryTemplateVersion.attributes.steps;
    const initialStep = firstInitialStep(steps);
    if (isUiStepConfig(initialStep)) {
      return headerComponentIndex(initialStep.config.components) !== -1;
    }
    // should be false if initial step is not found or is not a UI step (no components)
    return false;
  }, [inquiryTemplateVersion.attributes.steps]);

  return (
    <ConfigContainer data-test='inquiry-template-theme__container'>
      <div>
        {inquiryTemplateVersion.attributes['enabled-locales'].length > 1 && (
          <TitleContainer>
            <Spacer height={1} />
            <SelectContainer>
              <FormControlLabel id='inquiry-template-theme__language-selector-label'>
                Language
              </FormControlLabel>
              <Select
                aria-labelledby='inquiry-template-theme__language-selector-label'
                onValueChange={(value) => handleSetLocale(value as InquiryIso6391LanguageCode)}
                value={locale}
              >
                {inquiryTemplateVersion.attributes['enabled-locales'].map((enabledLocale) => (
                  <Option key={enabledLocale} value={enabledLocale}>
                    {INQUIRY_LANGUAGES_ENGLISH[enabledLocale]}
                  </Option>
                ))}
              </Select>
            </SelectContainer>
            <Spacer height={2} />
          </TitleContainer>
        )}

        <ThemeEditorNav
          showStartPage={false}
          showSelectCountryPage={false}
          showDocumentsUploadPage={false}
          showTerminalPages={false}
          showStartLogoPage={showStartLogoPage}
        />
      </div>
    </ConfigContainer>
  );
};

export default InquiryTemplateTheme;
